import { effectiveElementNames } from './conversions';
import { columnsOf, isRepeatingGroup } from '../config/repeatingGroups';

/**
 * The card editor's own save-time questions about repeating groups (KPR-679),
 * asked before the round trip so the sentence lands on the row that needs the
 * answer. Each mirrors a refusal the API makes anyway (keepr-api
 * utils/repeatingGroups.js), so the two always agree; the API stays the
 * authority, and a number the editor lets through (a maximum past the
 * ceiling, a minimum past the maximum) is the API's to refuse in its own
 * words.
 *
 * Every answer names the group by `element` (its effective name — the name
 * the API will give a new one) and `groupIndex`, and a column by `column` and
 * `columnIndex`, which is how RepeatingGroupOptions finds the row.
 */

const labelOf = el => (el?.label?.singular || '').trim();

/** Every group of the card with its position and the name it will save under. */
function groupsOf(elements) {
	const list = Array.isArray(elements) ? elements : [];
	const names = effectiveElementNames(list);
	return list
		.map((element, groupIndex) => ({ element, groupIndex, name: names[groupIndex] }))
		.filter(({ element }) => isRepeatingGroup(element));
}

/**
 * The first column that still has no data type, or null. A column is born
 * untyped (as an element is) and every option it offers depends on the
 * answer; the API refuses the same payload with 400 `invalid_element_type`.
 */
export function firstUntypedColumn(elements) {
	for (const { element, groupIndex, name } of groupsOf(elements)) {
		const columns = columnsOf(element);
		const names = effectiveElementNames(columns);
		const columnIndex = columns.findIndex(column => !column?.dataType);
		if (columnIndex >= 0) {
			return {
				groupIndex,
				element: name,
				groupLabel: labelOf(element),
				columnIndex,
				column: names[columnIndex],
				columnLabel: labelOf(columns[columnIndex]),
			};
		}
	}
	return null;
}

/** The first group with no column at all, or null (the API: `invalid_options`, option `columns`). */
export function firstEmptyGroup(elements) {
	const hit = groupsOf(elements).find(({ element }) => columnsOf(element).length === 0);
	return hit ? { groupIndex: hit.groupIndex, element: hit.name, groupLabel: labelOf(hit.element) } : null;
}

/**
 * Does any of these cards hold a repeating group (its own element, an
 * inherited one or a set's — `getElementsForCard` resolves them)? The export
 * dialog and the stored-export editor ask it before they offer Excel's
 * "One line per entry" (KPR-685): a choice the file would not show is not
 * offered.
 */
export function cardsHoldRepeatingGroup(cards, getElementsForCard) {
	return (Array.isArray(cards) ? cards : []).some((card) => {
		if (!card) return false;
		const resolved = typeof getElementsForCard === 'function' ? getElementsForCard(card._id || card.id) : null;
		const elements = Array.isArray(resolved) && resolved.length ? resolved : card.elements;
		return Array.isArray(elements) && elements.some(isRepeatingGroup);
	});
}
